import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ThreadListResponse,
  ThreadDetail,
  MessageListResponse,
  ExecutionTrace,
  ChatFilters,
} from '@/types/chat';

export const QUERY_KEY_CHAT_THREADS = 'chat-threads';
export const QUERY_KEY_CHAT_THREAD = 'chat-thread';
export const QUERY_KEY_CHAT_MESSAGES = 'chat-messages';
export const QUERY_KEY_CHAT_TRACE = 'chat-trace';

/**
 * List threads with filters + pagination
 */
export function useChatThreads(filters: ChatFilters = {}) {
  return useQuery<ThreadListResponse>({
    queryKey: [QUERY_KEY_CHAT_THREADS, filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters.phoneNumber) params.phoneNumber = filters.phoneNumber;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;

      const { data } = await api.get<ThreadListResponse>('/chat/threads', { params });
      return data;
    },
    staleTime: 0, // Always fresh for debug tool
  });
}

/**
 * Single thread detail (id + phoneNumber + messages)
 */
export function useChatThread(threadId: string) {
  return useQuery<ThreadDetail>({
    queryKey: [QUERY_KEY_CHAT_THREAD, threadId],
    queryFn: async () => {
      const { data } = await api.get<ThreadDetail>(`/chat/threads/${threadId}`);
      return data;
    },
    enabled: !!threadId,
    refetchInterval: 2000, // Poll full thread so allMessages never misses the OUTGOING response
    staleTime: 0,          // Always refetch fresh — critical for real-time message display
  });
}

/**
 * Polling for new messages via cursor-based fetch (refetchInterval: 2000ms per spec)
 */
export function usePollingMessages(threadId: string, lastMessageId?: string) {
  return useQuery<MessageListResponse>({
    queryKey: [QUERY_KEY_CHAT_MESSAGES, threadId],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (lastMessageId) params.since = lastMessageId;

      const { data } = await api.get<MessageListResponse>(
        `/chat/threads/${threadId}/messages`,
        { params },
      );
      return data;
    },
    refetchInterval: 2000, // CRITICAL: 2s polling per spec (RF11)
    enabled: !!threadId,
    staleTime: 0,
  });
}

/**
 * Message execution trace (triggered on AI message inspector open)
 * Handles aggregation delays with retry logic
 */
export function useMessageTrace(threadId: string, messageId: string | null) {
  return useQuery<ExecutionTrace>({
    queryKey: [QUERY_KEY_CHAT_TRACE, threadId, messageId],
    queryFn: async () => {
      const { data } = await api.get<ExecutionTrace>(
        `/chat/threads/${threadId}/messages/${messageId}/trace`,
      );
      return data;
    },
    enabled: !!threadId && !!messageId,
    retry: (failureCount, error: unknown) => {
      // Retry on 400 with "aggregation has not completed yet" message
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      const is400Aggregating =
        axiosError?.response?.status === 400 &&
        axiosError?.response?.data?.message?.includes('trace aggregation has not completed');

      if (is400Aggregating && failureCount < 5) {
        return true; // Retry up to 5 times
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 10000),
  });
}

interface DeleteThreadVariables {
  threadId: string;
  scope: 'trace_only' | 'full';
}

/**
 * Delete thread mutation (scope=trace_only or full)
 * Invalidates thread list and thread detail on success.
 */
export function useDeleteThread() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteThreadVariables>({
    mutationFn: async ({ threadId, scope }) => {
      await api.delete(`/chat/threads/${threadId}`, { params: { scope } });
    },
    onSuccess: (_data, { threadId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CHAT_THREADS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CHAT_THREAD, threadId] });
    },
  });
}
